import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../api";

const EMPTY_CELL = { view: false, add: false, edit: false, delete: false, export: false };

const RightsContext = createContext({
  bySubmenuId: {},
  byPathname: {},
  loading: true,
  refresh: () => {},
});
//
function normalizePath(p) {
  let path = String(p || "").trim();
  if (!path.startsWith("/")) path = "/" + path;
  return path.toLowerCase();
}

// Wrap the app (inside SaamiTradestarLogistics.jsx, or around it in App.js —
// either works, as long as it's above every menu component in the tree) with
// <RightsProvider>...</RightsProvider>. It fetches the logged-in user's
// merged action-rights maps exactly once, and every screen reads from it via
// useRights() below — no separate fetch per menu file.
export function RightsProvider({ children }) {
  const [bySubmenuId, setBySubmenuId] = useState({});
  const [byPathname, setByPathname] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/RoleMaster/my-rights");
      setBySubmenuId(res.data?.bySubmenuId || {});
      setByPathname(res.data?.byPathname || {});
    } catch (err) {
      console.error("Failed to load menu rights:", err);
      setBySubmenuId({});
      setByPathname({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <RightsContext.Provider value={{ bySubmenuId, byPathname, loading, refresh: load }}>
      {children}
    </RightsContext.Provider>
  );
}

// Every menu component calls this with its OWN route/pathname — the same
// string already sitting in Submenus.pathname for that screen — instead of
// a hardcoded numeric Submenu_id. Case/leading-slash don't matter, both
// sides get normalized the same way.
//
// Example:
//   const rights = useRights("/PartyMaster");
//   {rights.add && <button onClick={openCreate}>+ Add Party</button>}
//
// A numeric Submenu_id still works too, if you ever need it:
//   const rights = useRights(25);
export function useRights(key) {
  const { bySubmenuId, byPathname, loading } = useContext(RightsContext);

  let cell = EMPTY_CELL;
  if (typeof key === "number" || /^\d+$/.test(String(key))) {
    cell = bySubmenuId[String(key)] || EMPTY_CELL;
  } else if (key) {
    cell = byPathname[normalizePath(key)] || EMPTY_CELL;
  }

  return { ...cell, loading };
}

// Optional: call this after Role Master saves rights for a role that might
// affect the CURRENTLY logged-in user, so their own buttons update without
// requiring a fresh login.
export function useRightsRefresh() {
  const { refresh } = useContext(RightsContext);
  return refresh;
}